import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronDownIcon, ExclamationIcon } from '@heroicons/react/solid';
import { MailIcon } from '@heroicons/react/outline';
import { functions, db } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ExportWizardDialog from './ExportWizardDialog';
import ExportProformaDialog from './ExportProformaDialog';
import MessageHistoryDialog from './MessageHistoryDialog';
import ProviderManageInformantsForm from './ProviderManageInformantsForm';
import ReassignCliniciansDialog from './ReassignCliniciansDialog';
import AssignCliniciansDialog from './AssignCliniciansDialog';
import ProviderEditPatientForm from './ProviderEditPatientForm';
import { useAuth } from '../../../../hooks/useAuth';
import useWorkflowConfig from '../../../../hooks/useWorkflowConfig';
import PatientContractTypeBadge from '../../../../components/PatientContractTypeBadge';
import CypGuardianContactLines from '../../../../components/CypGuardianContactLines';
import {
  isClinicianAssignedToPatient,
  isSSILeadOverrideActive,
} from '../../../../utils/clinicianPermissions';
import { toast } from 'react-toastify';
import { formatDateTime } from '../../../../utils/timestampUtils';

export default function PatientInfoPanel({
  patientDataModel,
  patientId,
  informantData = {},
  refreshPatientData,
  isSubclinicalService = false,
  requestReassignStep = null,
  onReassignStepHandled,
  documentsRoute,
  // Optional: opens Manage Appointment Dates (same dialog as module-card Add Date).
  // Only passed when the org has appointment dates enabled (SLaM / 3 Borough).
  onManageAppointmentDates,
  // CYP report documents. Manage parents and school uses them to keep
  // unlock/re-lock disabled until the first appointment section exists.
  reportContent = {},
}) {  
  const [showAdminButtons, setShowAdminButtons] = useState(false);
  const [canEditPatientDetails, setCanEditPatientDetails] = useState(false);
  const [showQbTest, setShowQbTest] = useState(false);
  const [showPhysicalHealthButton, setShowPhysicalHealthButton] = useState(false);
  const [isQbTestDialogOpen, setIsQbTestDialogOpen] = useState(false);
  const [qbTestFormData, setQbTestFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [originalQbTestData, setOriginalQbTestData] = useState({});
  const [showQbTestApprovalBanner, setShowQbTestApprovalBanner] = useState(false);
  const [qbTestApprovalInfo, setQbTestApprovalInfo] = useState(null);
  const [isPhysicalHealthDialogOpen, setIsPhysicalHealthDialogOpen] = useState(false);
  const [physicalHealthFormData, setPhysicalHealthFormData] = useState({});
  const [isPhysicalHealthSubmitting, setIsPhysicalHealthSubmitting] = useState(false);
  const [physicalHealthSubmitError, setPhysicalHealthSubmitError] = useState('');
  const [originalPhysicalHealthData, setOriginalPhysicalHealthData] = useState({});
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isProformaExportOpen, setIsProformaExportOpen] = useState(false);
  const [isManageInformantsOpen, setIsManageInformantsOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isMessageHistoryOpen, setIsMessageHistoryOpen] = useState(false);
  const [isReassignDialogOpen, setIsReassignDialogOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isEditPatientOpen, setIsEditPatientOpen] = useState(false);

  // Lead SSI takeover (SLaM / 3 Borough): while the override is active the Lead
  // owns SSI editing/review, but the original clinicianSSI attribution slot is
  // intentionally left unchanged. Reassigning clinicians mid-takeover would
  // muddle ownership (and a Lead swap would silently transfer the override),
  // so we freeze reassignment until the takeover is resolved.
  const isLeadOverrideActive = isSSILeadOverrideActive(patientDataModel);

  // When an external entry point (e.g. the "+" button on the DD module card) wants
  // to open this dialog at a specific wizard step, it sets requestReassignStep.
  // This effect opens the dialog automatically in response — except while a Lead
  // SSI override is active, in which case we clear the request instead.
  useEffect(() => {
    if (!requestReassignStep) return;
    if (isLeadOverrideActive) {
      onReassignStepHandled?.();
      return;
    }
    setIsReassignDialogOpen(true);
  }, [requestReassignStep, isLeadOverrideActive, onReassignStepHandled]);

  // Resend welcome email state
  const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [lastWelcomeEmailSentAt, setLastWelcomeEmailSentAt] = useState(null);
  const [isLoadingLastSent, setIsLoadingLastSent] = useState(false);

  // New state: Track whether current user can reassign clinicians and in what capacity
  // - canReassignClinicians: true if user is Admin/Support OR the Lead Clinician for this patient
  // - isLeadClinicianMode: true if user is Lead Clinician (not admin/support) - restricts what they can change
  const [canReassignClinicians, setCanReassignClinicians] = useState(false);
  const [isLeadClinicianMode, setIsLeadClinicianMode] = useState(false);

  // Get claims from useAuth hook - no token refresh needed!
  const { claims, user } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Feature flag: Proforma JSON export is currently only enabled for Well Life Clinic.
  // This is gated on the clinician's organisation (from auth claims) so that only
  // WLC clinicians see the action. Can be expanded to other orgs by adding conditions
  // here or migrating to a workflow config flag in the future.
  const isProformaExportEnabled = claims?.organisation?.includes('Well Life') || false;

  // Check user claims and patient feature flags to determine which buttons should be shown
  // Memoize permission checks to avoid recalculating on every render
  useEffect(() => {
    if (!claims) {
      setShowAdminButtons(false);
      setCanEditPatientDetails(false);
      setShowQbTest(false);
      setShowPhysicalHealthButton(false);
      setCanReassignClinicians(false);
      setIsLeadClinicianMode(false);
      return;
    }

    const userType = claims.userType;
    const isAdminUser = userType === 'Support' || userType === 'Org. Admin';
    
    // Physical health: still uses the patient-level flag (separate concern, not migrated)
    const patientIncludesPhysicalHealth = patientDataModel?.workflowIncludesPhysicalHealthQuestions !== false;
    
    // -------------------------------------------------------------------------
    // Clinician Reassignment Permissions
    // -------------------------------------------------------------------------
    // Business context:
    // - Org Admins and Support users can reassign any clinician (including Lead).
    // - Lead Clinicians (assigned to THIS patient) can reassign supporting clinicians
    //   and primary assignees for CI/SSI/DD/PAD, but CANNOT change the Lead role.
    // - This enables quick MDT adjustments without requiring admin intervention.
    //
    // The isLeadClinicianMode flag tells the dialog to disable the "Change Lead" action.
    // -------------------------------------------------------------------------
    const leadClinicianId = patientDataModel?.clinicians?.clinicianLead?.clinicianId;
    const currentUserId = user?.uid;
    const isLeadClinicianForPatient = leadClinicianId && currentUserId && leadClinicianId === currentUserId;
    
    // User can reassign if they are Admin/Support OR the Lead Clinician for this patient.
    // Lead Clinicians get a restricted view (isLeadClinicianMode) where they can only
    // add an MDT Review Clinician — they cannot change the Lead or reassign others.
    const userCanReassign = isAdminUser || isLeadClinicianForPatient;
    
    // Lead Clinician mode: when the user is the Lead (not admin/support), restrict Lead changes
    const inLeadClinicianMode = isLeadClinicianForPatient && !isAdminUser; 
    
    // Assigned clinicians can edit patient details (name) alongside admin users.
    // The Cloud Function already authorises this; this aligns the UI to match.
    const isAssignedClinician = userType === 'Clinician'
      && isClinicianAssignedToPatient(patientDataModel, currentUserId);

    setShowAdminButtons(isAdminUser);
    setCanEditPatientDetails(isAdminUser || isAssignedClinician);
    setShowQbTest(wf.isQbTestEnabled);
    setShowPhysicalHealthButton(isAdminUser && wf.isQbTestEnabled && patientIncludesPhysicalHealth);
    setCanReassignClinicians(userCanReassign);
    setIsLeadClinicianMode(inLeadClinicianMode);
  }, [claims, user, wf.isQbTestEnabled, patientDataModel]);

  // Detect whether this patient has clinicians assigned yet.
  // Some organisations (e.g. The Owl Centre) create patients without clinician
  // assignments and assign them retroactively. When no Lead clinician is set,
  // we show "Assign Clinicians" instead of "Reassign Clinicians" in the actions menu.
  const hasCliniciansAssigned = useMemo(() => {
    const lead = patientDataModel?.clinicians?.clinicianLead;
    return !!(lead && typeof lead === 'object' && lead.clinicianId);
  }, [patientDataModel?.clinicians?.clinicianLead]);

  // Check if QbTest data has been entered
  const qbTestDataExists = !!(
    patientDataModel?.dataModel?.['6_manageReports']?.qbTestReportGenerated
  );

  // Find the "Your Physical Health" subsection dynamically by title so this
  // works for both legacy patients (subsection key '1') and new patients
  // (key '7') without hardcoding any particular position.
  const physicalHealthSubsection = useMemo(() => {
    const subs = patientDataModel?.formData?.yourHealth?.formSubsections;
    if (!subs) return null;
    return Object.values(subs).find(
      (sub) => sub?.formSubsectionTitle === "Your Physical Health"
    ) || null;
  }, [patientDataModel?.formData?.yourHealth?.formSubsections]);

  // Check if Physical Health data has been submitted
  const physicalHealthDataExists = !!(
    patientDataModel?.dataModel?.['2_clinicalInterview']?.healthInfo ||
    patientDataModel?.physicalHealthInfoUpdatedAt ||
    // Also check formData as fallback via the dynamically-found subsection
    (physicalHealthSubsection?.questions?.['1']?.subquestions?.['1']?.answer ||
     physicalHealthSubsection?.questions?.['2']?.subquestions?.['1']?.answer ||
     physicalHealthSubsection?.questions?.['3']?.subquestions?.['1']?.answer ||
     physicalHealthSubsection?.questions?.['4']?.subquestions?.['1']?.answer)
  );

  // CYP ADHD patients are children without a login account or email address
  // (hasAuthAccount: false on the header). Several adult-only actions below
  // are gated on this flag.
  const isCypAdhd = patientDataModel?.assessmentModule === 'cypAdhd';

  // Show the resend welcome email action to admin-level users. Enabled only
  // before the patient has completed account setup; otherwise greyed out with
  // an explanatory note so providers know the capability exists.
  // Hidden entirely for CYP ADHD: the child has no account, so there is no
  // patient welcome email. Parent/carer and school representative welcome
  // resends live in Manage Informants.
  const showResendWelcomeEmail = showAdminButtons && !isCypAdhd;
  const canResendWelcomeEmail = showAdminButtons && patientDataModel?.hasSetOwnPassword === false;

  /**
   * Query messageHistory for the most recent welcome email sent to this patient.
   * Used to display "Last sent: ..." in the confirmation dialog so the provider
   * can make an informed decision about whether to resend.
   */
  const fetchLastWelcomeEmailTimestamp = useCallback(async () => {
    if (!patientId) return;
    setIsLoadingLastSent(true);
    try {
      const mhRef = collection(db, 'patients', patientId, 'messageHistory');
      const mhQuery = query(
        mhRef,
        where('type', '==', 'patient_welcome'),
        orderBy('sentAt', 'desc'),
        limit(1)
      );
      const snap = await getDocs(mhQuery);
      if (!snap.empty) {
        setLastWelcomeEmailSentAt(snap.docs[0].data().sentAt);
      } else {
        setLastWelcomeEmailSentAt(null);
      }
    } catch (err) {
      console.error('Failed to fetch last welcome email timestamp:', err);
      setLastWelcomeEmailSentAt(null);
    } finally {
      setIsLoadingLastSent(false);
    }
  }, [patientId]);

  /**
   * Open the resend confirmation dialog and kick off the messageHistory lookup
   * so the timestamp is ready by the time the dialog renders.
   */
  const handleResendClick = useCallback(() => {
    setIsResendDialogOpen(true);
    fetchLastWelcomeEmailTimestamp();
  }, [fetchLastWelcomeEmailTimestamp]);

  /**
   * Call the resendWelcomeEmail CF after the provider confirms. On success we
   * show a toast and close the dialog; on failure we show an error toast with
   * the backend message.
   */
  const handleResendConfirm = useCallback(async () => {
    setIsResendLoading(true);
    try {
      const resendFn = httpsCallable(functions, 'resendWelcomeEmail');
      await resendFn({ targetUserId: patientId, patientId });
      toast.success(`Welcome email resent to ${patientDataModel?.email}`);
      setIsResendDialogOpen(false);
    } catch (err) {
      const message = err?.message || 'Failed to resend welcome email';
      toast.error(message);
    } finally {
      setIsResendLoading(false);
    }
  }, [patientId, patientDataModel?.email]);

  // QbTest form fields definition
  const qbTestFields = useMemo(() => [
    {
      varName: "dateOfQbTest",
      displayText: "QbTest Date",
      inputType: "date",
      required: false,
    },
    {
      varName: "qbActivityResults",
      displayText: "Summary of QbActivity Results",
      inputType: "freeTextArea",
      placeholder: "Provide a detailed summary of the QbActivity results",
      required: false,
    },
    {
      varName: "qbInattentionResults",
      displayText: "Summary of QbInattention Results",
      inputType: "freeTextArea",
      placeholder: "Provide a detailed summary of the QbInattention results",
      required: false,
    },
    {
      varName: "qbImpulsivityResults",
      displayText: "Summary of QbImpulsivity Results",
      inputType: "freeTextArea",
      placeholder: "Provide a detailed summary of the QbImpulsivity results",
      required: false,
    },
  ], []);

  // Physical Health form fields definition
  const physicalHealthFields = useMemo(() => [
    {
      varName: "height",
      displayText: "Height (cm)",
      inputType: "freeTextNumerical",
      placeholder: "Enter height in centimeters",
      required: false,
    },
    {
      varName: "weight",
      displayText: "Weight (kg)",
      inputType: "freeTextNumerical",
      placeholder: "Enter weight in kilograms",
      required: false,
    },
    {
      varName: "bloodPressureSystolic",
      displayText: "Blood Pressure - Systolic",
      inputType: "freeTextNumerical",
      placeholder: "Enter systolic blood pressure",
      required: false,
    },
    {
      varName: "bloodPressureDiastolic",
      displayText: "Blood Pressure - Diastolic",
      inputType: "freeTextNumerical",
      placeholder: "Enter diastolic blood pressure",
      required: false,
    },
    {
      varName: "pulse",
      displayText: "Pulse (bpm)",
      inputType: "freeTextNumerical",
      placeholder: "Enter pulse in beats per minute",
      required: false,
    },
  ], []);

  // Initialize QbTest form data when dialog opens
  useEffect(() => {
    if (isQbTestDialogOpen) {
      const initialData = {};

      // If editing existing data, pre-fill with current values
      if (qbTestDataExists) {
        const qbTestSection = patientDataModel?.dataModel?.['6_manageReports']?.qbTest?.['1'];
        
        if (qbTestSection) {
          initialData.dateOfQbTest = qbTestSection.dateOfQbTest?.answer?.value || '';
          initialData.qbActivityResults = qbTestSection.qbActivityResults?.answer?.value || '';
          initialData.qbInattentionResults = qbTestSection.qbInattentionResults?.answer?.value || '';
          initialData.qbImpulsivityResults = qbTestSection.qbImpulsivityResults?.answer?.value || '';
        } else {
          // Initialize with empty values if no existing data
          qbTestFields.forEach(field => {
            if (field?.varName) {
              initialData[field.varName] = '';
            }
          });
        }
      } else {
        // Initialize with empty values for new data
        qbTestFields.forEach(field => {
          if (field?.varName) {
            initialData[field.varName] = '';
          }
        });
      }

      setQbTestFormData(initialData);
      setOriginalQbTestData(initialData); // Store original data for comparison
      setSubmitError('');

      // Check if report is already approved and set up approval banner
      const reportApproved = patientDataModel?.dataModel?.['6_manageReports']?.qbTestReportApproved;
      if (reportApproved) {
        const approvalInfo = {
          approvedBy: patientDataModel?.dataModel?.['6_manageReports']?.qbTestReportApprovedByName || 'Unknown',
          approvedAt: patientDataModel?.dataModel?.['6_manageReports']?.qbTestReportApprovedAt
        };
        
        // Format the approval date
        if (approvalInfo.approvedAt && approvalInfo.approvedAt.toDate) {
          try {
            approvalInfo.approvedAtFormatted = approvalInfo.approvedAt.toDate().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          } catch (error) {
            approvalInfo.approvedAtFormatted = 'Unknown date';
          }
        } else if (approvalInfo.approvedAt && approvalInfo.approvedAt.seconds) {
          // Handle Firestore timestamp format
          try {
            const date = new Date(approvalInfo.approvedAt.seconds * 1000);
            approvalInfo.approvedAtFormatted = date.toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          } catch (error) {
            approvalInfo.approvedAtFormatted = 'Unknown date';
          }
        } else {
          approvalInfo.approvedAtFormatted = 'Unknown date';
        }
        
        setQbTestApprovalInfo(approvalInfo);
        setShowQbTestApprovalBanner(true);
      } else {
        setShowQbTestApprovalBanner(false);
        setQbTestApprovalInfo(null);
      }
    }
  }, [isQbTestDialogOpen, qbTestFields, qbTestDataExists, patientDataModel]);

  // Initialize Physical Health form data when dialog opens
  useEffect(() => {
    if (isPhysicalHealthDialogOpen) {
      const initialData = {};

      // If editing existing data, pre-fill with current values
      if (physicalHealthDataExists) {
        const healthInfo = patientDataModel?.dataModel?.['2_clinicalInterview']?.healthInfo;
        
        // Try to get data from dataModel first, then fallback to formData if needed
        if (healthInfo) {
          initialData.height = healthInfo.height?.value || '';
          initialData.weight = healthInfo.weight?.value || '';
          initialData.pulse = healthInfo.pulse?.value || '';
          initialData.bloodPressureSystolic = healthInfo.bloodPressure?.systolic || '';
          initialData.bloodPressureDiastolic = healthInfo.bloodPressure?.diastolic || '';
        } else {
          // Fallback: try to extract from formData if dataModel doesn't have it,
          // using the dynamically-found Physical Health subsection.
          const healthSection = physicalHealthSubsection?.questions;
          if (healthSection) {
            initialData.weight = healthSection['1']?.subquestions?.['1']?.answer || '';
            initialData.height = healthSection['2']?.subquestions?.['1']?.answer || '';
            initialData.pulse = healthSection['3']?.subquestions?.['1']?.answer || '';
            initialData.bloodPressureSystolic = healthSection['4']?.subquestions?.['1']?.answer || '';
            initialData.bloodPressureDiastolic = healthSection['4']?.subquestions?.['2']?.answer || '';
          }
        }
      } else {
        // Initialize with empty values for new data
        physicalHealthFields.forEach(field => {
          if (field?.varName) {
            initialData[field.varName] = '';
          }
        });
      }

      setPhysicalHealthFormData(initialData);
      setOriginalPhysicalHealthData(initialData); // Store original data for comparison
      setPhysicalHealthSubmitError('');
    }
  }, [isPhysicalHealthDialogOpen, physicalHealthFields, physicalHealthDataExists, patientDataModel, physicalHealthSubsection?.questions]);

  // Handle QbTest form input changes
  const handleQbTestInputChange = (fieldName, value) => {
    setQbTestFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handle Physical Health form input changes
  const handlePhysicalHealthInputChange = (fieldName, value) => {
    setPhysicalHealthFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Validate QbTest form
  const isQbTestFormValid = () => {
    // Check that date field is not empty
    const dateValue = qbTestFormData.dateOfQbTest;
    const isDateValid = dateValue && dateValue.trim() !== '';
    
    // Check that at least one text area is not empty
    const textAreas = ['qbActivityResults', 'qbInattentionResults', 'qbImpulsivityResults'];
    const isAtLeastOneTextAreaFilled = textAreas.some(fieldName => {
      const value = qbTestFormData[fieldName];
      return value && value.trim() !== '';
    });
    
    // Both date and at least one text area must be filled
    const basicValidation = isDateValid && isAtLeastOneTextAreaFilled;
    
    if (!basicValidation) return false;
    
    if (qbTestDataExists) {
      // For editing existing data: also check if anything has changed
      const hasChanged = qbTestFields.some(field => {
        const currentValue = qbTestFormData[field.varName] || '';
        const originalValue = originalQbTestData[field.varName] || '';
        return currentValue !== originalValue;
      });
      
      return basicValidation && hasChanged;
    }
    
    // For new data: basic validation is sufficient
    return basicValidation;
  };

  // Validate Physical Health form
  const isPhysicalHealthFormValid = () => {
    // Check required fields first (if any)
    const requiredFieldsValid = physicalHealthFields.every(field => {
      if (field.required !== true) return true;
      
      const value = physicalHealthFormData[field.varName];
      return value !== undefined && value !== '';
    });
    
    if (!requiredFieldsValid) return false;
    
    if (physicalHealthDataExists) {
      // For editing existing data: check if anything has changed
      return physicalHealthFields.some(field => {
        const currentValue = physicalHealthFormData[field.varName] || '';
        const originalValue = originalPhysicalHealthData[field.varName] || '';
        return currentValue !== originalValue;
      });
    } else {
      // For new data: at least one field must be filled
      return physicalHealthFields.some(field => {
        const value = physicalHealthFormData[field.varName];
        return value && value.trim() !== '';
      });
    }
  };

  // Handle QbTest form submission
  const handleQbTestSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Call the updateQbTestResults cloud function
      const updateQbTestResults = httpsCallable(functions, 'updateQbTestResults');
      const result = await updateQbTestResults({
        patientId: patientId,
        dateOfQbTest: qbTestFormData.dateOfQbTest || '',
        qbActivityResults: qbTestFormData.qbActivityResults || '',
        qbInattentionResults: qbTestFormData.qbInattentionResults || '',
        qbImpulsivityResults: qbTestFormData.qbImpulsivityResults || ''
      });

      console.log('QbTest results updated successfully:', result.data);
      
      // Close dialog and reset form
      setIsQbTestDialogOpen(false);
      setQbTestFormData({});
      
      // Refresh patient data to update UI with latest information
      if (refreshPatientData) {
        refreshPatientData();
      }
      
    } catch (error) {
      console.error('Error submitting QbTest form:', error);
      
      // Handle specific error messages from cloud function
      if (error.code === 'functions/permission-denied') {
        setSubmitError('You do not have permission to update QbTest results.');
      } else if (error.code === 'functions/invalid-argument') {
        setSubmitError('Invalid data provided. Please check your inputs and try again.');
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An error occurred while submitting the form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Physical Health form submission
  const handlePhysicalHealthSubmit = async () => {
    setIsPhysicalHealthSubmitting(true);
    setPhysicalHealthSubmitError('');

    try {
      // Call the updatePhysicalHealthInfo cloud function
      const updatePhysicalHealthInfo = httpsCallable(functions, 'updatePhysicalHealthInfo');
      const result = await updatePhysicalHealthInfo({
        patientId: patientId,
        height: physicalHealthFormData.height || '',
        weight: physicalHealthFormData.weight || '',
        bloodPressureSystolic: physicalHealthFormData.bloodPressureSystolic || '',
        bloodPressureDiastolic: physicalHealthFormData.bloodPressureDiastolic || '',
        pulse: physicalHealthFormData.pulse || ''
      });

      console.log('Physical Health info updated successfully:', result.data);
      
      // Close dialog and reset form
      setIsPhysicalHealthDialogOpen(false);
      setPhysicalHealthFormData({});
      
      // Refresh patient data to update UI with latest information
      if (refreshPatientData) {
        refreshPatientData();
      }
      
    } catch (error) {
      console.error('Error submitting Physical Health form:', error);
      
      // Handle specific error messages from cloud function
      if (error.code === 'functions/permission-denied') {
        setPhysicalHealthSubmitError('You do not have permission to update physical health information.');
      } else if (error.code === 'functions/invalid-argument') {
        setPhysicalHealthSubmitError('Invalid data provided. Please check your inputs and try again.');
      } else if (error.message) {
        setPhysicalHealthSubmitError(error.message);
      } else {
        setPhysicalHealthSubmitError('An error occurred while submitting the form. Please try again.');
      }
    } finally {
      setIsPhysicalHealthSubmitting(false);
    }
  };

  // Render QbTest form field
  const renderQbTestField = (field) => {
    if (!field || !field.varName) return null;

    const { varName, displayText, inputType, placeholder, required } = field;

    switch (inputType) {
      case 'date':
        return (
          <div key={varName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {displayText}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={qbTestFormData[varName] || ''}
              onChange={(e) => handleQbTestInputChange(varName, e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        );

      case 'freeTextArea':
        return (
          <div key={varName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {displayText}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={qbTestFormData[varName] || ''}
              onChange={(e) => handleQbTestInputChange(varName, e.target.value)}
              placeholder={placeholder}
              rows={4}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 resize-vertical"
            />
          </div>
        );

      default:
        return (
          <div key={varName} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {displayText}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={qbTestFormData[varName] || ''}
              onChange={(e) => handleQbTestInputChange(varName, e.target.value)}
              placeholder={placeholder}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        );
    }
  };

  // Render Physical Health form field
  const renderPhysicalHealthField = (field) => {
    if (!field || !field.varName) return null;

    const { varName, displayText, inputType, placeholder, required } = field;

    // Special handling for blood pressure - render systolic and diastolic together
    if (varName === 'bloodPressureSystolic') {
      return (
        <div key="bloodPressure" className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Blood Pressure{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <input
                type="number"
                value={physicalHealthFormData.bloodPressureSystolic || ''}
                onChange={(e) => handlePhysicalHealthInputChange('bloodPressureSystolic', e.target.value)}
                placeholder="Systolic"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <span className="text-gray-500 font-medium">/</span>
            <div className="flex-1">
              <input
                type="number"
                value={physicalHealthFormData.bloodPressureDiastolic || ''}
                onChange={(e) => handlePhysicalHealthInputChange('bloodPressureDiastolic', e.target.value)}
                placeholder="Diastolic"
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <span className="text-gray-500 text-sm">mmHg</span>
          </div>
        </div>
      );
    }

    // Skip rendering diastolic separately since it's handled with systolic
    if (varName === 'bloodPressureDiastolic') {
      return null;
    }

    // Get the appropriate unit suffix based on the field
    const getUnitSuffix = (fieldName) => {
      switch (fieldName) {
        case 'height': return 'cm';
        case 'weight': return 'kg';
        case 'pulse': return 'bpm';
        default: return null;
      }
    };

    const unitSuffix = getUnitSuffix(varName);

    if (inputType === 'freeTextNumerical') {
      return (
        <div key={varName} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {displayText}{required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={physicalHealthFormData[varName] || ''}
              onChange={(e) => handlePhysicalHealthInputChange(varName, e.target.value)}
              placeholder={placeholder}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            {unitSuffix && (
              <span className="text-gray-500 text-sm">{unitSuffix}</span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={varName} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {displayText}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type="text"
          value={physicalHealthFormData[varName] || ''}
          onChange={(e) => handlePhysicalHealthInputChange(varName, e.target.value)}
          placeholder={placeholder}
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
        />
      </div>
    );
  };

  // Handle export button click
  const handleExportClick = () => {
    setIsExportDialogOpen(true);
  };

  // Copies the current patient's ID to the clipboard and shows a toast.
  // Design intent: keep behavior consistent with the dashboard patient table,
  // giving providers a quick way to reference the UID for support/logs.
  const copyPatientIdToClipboard = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success(`Patient ID ${id} has been copied to clipboard`);
    } catch (err) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = id;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success(`Patient ID ${id} has been copied to clipboard`);
      } catch (fallbackErr) {
        console.error('Failed to copy patient ID:', fallbackErr);
      }
    }
  };

  return (
    <>
    <div className="w-full px-6 py-5 bg-gray-50 rounded-xl border border-gray-200 justify-start items-center gap-14 inline-flex text-left">
      <div className="grow shrink basis-0 h-16 justify-start items-center gap-5 flex">
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
          <div className="self-stretch text-gray-900 text-3xl font-semibold leading-9">
            {patientDataModel.firstName} {patientDataModel.lastName}
          </div>
          <div className="self-stretch text-slate-800 text-base font-normal leading-normal">
            {patientDataModel.email}
          </div>
          {/* CYP: the child has no account of their own, so the header names
              the person the clinic actually contacts — the parent/carer.
              Shared with the CYP Documents header so the two stay identical;
              the component's own comment records the design decisions. */}
          {isCypAdhd && (
            <CypGuardianContactLines informants={patientDataModel?.informants} />
          )}
        </div>
      </div>      
        <div className="flex justify-start items-center gap-3">
          {/* Contract-type ("project") badge, right-aligned before the action
              buttons. Renders nothing for orgs that don't categorise patients by
              contract type, in which case the actions simply sit flush right. */}
          <PatientContractTypeBadge
            label="Contract"
            organisation={claims?.organisation}
            assessmentType={patientDataModel?.customFields?.assessmentType}
            subContractType={patientDataModel?.customFields?.subContractType}
          />
          {/**
           * Compact actions menu: replaces multiple inline buttons with a single
           * hover-triggered dropdown. This keeps the header compact while
           * retaining access to the same actions. The violet "Go to Documents"
           * button remains distinct for quick navigation.
           */}
          <div
            className="relative"
            onMouseEnter={() => setIsActionsOpen(true)}
            onMouseLeave={() => setIsActionsOpen(false)}
          >
            <button
              className="px-4 py-2 font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 inline-flex items-center gap-1"
              type="button"
              aria-haspopup="menu"
              aria-expanded={isActionsOpen ? 'true' : 'false'}
            >
              View Actions
              <ChevronDownIcon className="w-4 h-4 text-gray-600" />
            </button>

            {/* Connected dropdown (no gap): positioned directly under the trigger */}
            <div
              className={`absolute right-0 top-full w-56 bg-white border border-gray-200 rounded-lg shadow-lg transition-opacity duration-150 z-20 ${
                isActionsOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
              role="menu"
            >
              <div className="py-1">
                <button
                  onClick={() => setIsManageInformantsOpen(true)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  type="button"
                >
                  {isCypAdhd ? 'Manage parents and school' : 'Manage Informants'}
                </button>

                {canEditPatientDetails && (
                  <button
                    onClick={() => setIsEditPatientOpen(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    Edit Patient Details
                  </button>
                )}

                <button
                  onClick={handleExportClick}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  type="button"
                >
                  Export Form Data
                </button>

                {isProformaExportEnabled && (
                  <button
                    onClick={() => setIsProformaExportOpen(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    Export Proforma JSON
                  </button>
                )}

                {/* 
                  Clinician Assignment / Reassignment:
                  - "Assign Clinicians": shown when no clinicians are assigned yet (initial assignment).
                    Only available to Admin/Support users.
                  - "Reassign Clinicians": shown when clinicians are already assigned (swap/change).
                    Available to Admin/Support (full access) AND Lead Clinicians (restricted mode).
                    Disabled while a Lead SSI takeover is active (see isLeadOverrideActive above).
                */}
                {canReassignClinicians && hasCliniciansAssigned && (
                  isLeadOverrideActive ? (
                    <div className="w-full text-left px-4 py-2" role="menuitem" aria-disabled="true">
                      <span className="text-sm text-gray-400">Reassign Clinicians</span>
                      <span className="block text-xs text-gray-400 mt-0.5">
                        Unavailable while a Lead SSI takeover is in progress
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsReassignDialogOpen(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      type="button"
                    >
                      Reassign Clinicians
                    </button>
                  )
                )}
                {showAdminButtons && !hasCliniciansAssigned && (
                  <button
                    onClick={() => setIsAssignDialogOpen(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    Assign Clinicians
                  </button>
                )}

                {/* Appointment dates (SLaM / 3 Borough): same multi-module dialog as
                    the per-card Add Date / edit controls, but without auto-opening a
                    native date picker — no specific module was chosen from here. */}
                {typeof onManageAppointmentDates === 'function' && (
                  <button
                    onClick={() => {
                      setIsActionsOpen(false);
                      onManageAppointmentDates();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    Manage Appointment Dates
                  </button>
                )}

                {showAdminButtons && showQbTest && (
                  <button
                    onClick={() => setIsQbTestDialogOpen(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    {qbTestDataExists ? 'Edit QbTest Results' : 'Add QbTest Results'}
                  </button>
                )}

                {showPhysicalHealthButton && (
                  <button
                    onClick={() => setIsPhysicalHealthDialogOpen(true)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    type="button"
                  >
                    {physicalHealthDataExists ? 'Edit Physical Health Info' : 'Add Physical Health Info'}
                  </button>
                )}

                {showResendWelcomeEmail && (
                  canResendWelcomeEmail ? (
                    <button
                      onClick={handleResendClick}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      type="button"
                    >
                      Resend Welcome Email
                    </button>
                  ) : (
                    <div className="w-full text-left px-4 py-2">
                      <span className="text-sm text-gray-400">Resend Welcome Email</span>
                      <span className="block text-xs text-gray-400 mt-0.5">
                        Only available before the patient has signed in
                      </span>
                    </div>
                  )
                )}

                <button
                  onClick={() => setIsMessageHistoryOpen(true)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  type="button"
                >
                  View email log
                </button>

                <button
                  onClick={() => copyPatientIdToClipboard(patientId)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  type="button"
                >
                  Copy ID
                </button>
              </div>
            </div>
          </div>

          {/* Hide Go to Documents button for subclinical services */}
          {!isSubclinicalService && (
            <Link
              to={documentsRoute || `/patient/${patientId}/documents`}
              className="inline-flex font-bold items-center gap-1 px-4 py-2 bg-violet-900 hover:bg-violet-800 text-white rounded-lg transition duration-100"
            >
              Go to Documents
              <ChevronRightIcon className="w-5 h-5" />
            </Link>
          )}
        </div>
    </div>

      {/* QbTest Results Dialog */}
      <AlertDialog.Root open={isQbTestDialogOpen} onOpenChange={setIsQbTestDialogOpen}>
        <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto z-50">
            <AlertDialog.Title className="text-xl font-semibold mb-4">
              {qbTestDataExists ? 'Edit QbTest Results' : 'Add QbTest Results'}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-500 mb-6">
              {qbTestDataExists 
                ? 'Update the QbTest results for this patient.'
                : 'Please provide the QbTest results for this patient.'
              }
            </AlertDialog.Description>

            <div className="space-y-4">
              {qbTestFields.map((field, index) => (
                <div key={field.varName}>
                  {index > 0 && (
                    <div className="h-px bg-gray-200 my-4" aria-hidden="true" />
                  )}
                  {renderQbTestField(field)}
                </div>
              ))}
            </div>

            {/* QbTest Approval Warning Banner */}
            <div className={`overflow-hidden transition-all duration-400 ease-in-out ${showQbTestApprovalBanner ? 'max-h-96 mt-6 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="bg-amber-50 rounded-lg p-4 text-left border border-amber-200">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <ExclamationIcon className="h-5 w-5 text-amber-600" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">QbTest Report already approved by {qbTestApprovalInfo?.approvedBy} on {qbTestApprovalInfo?.approvedAtFormatted}</h3>
                    <div className="mt-2 text-sm text-amber-700">                      
                      <p className="font-medium">
                        Submitting will regenerate report paragraphs and reset approval status. The new content will need to be reviewed and approved again.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {submitError && (
              <div className="p-3 mt-4 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
                {submitError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-semibold rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              
              <button
                type="button"
                onClick={handleQbTestSubmit}
                disabled={!isQbTestFormValid() || isSubmitting}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors
                  ${!isQbTestFormValid() || isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 hover:bg-violet-700'}`}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                                  ) : (
                    qbTestDataExists ? 'Update QbTest Results' : 'Submit QbTest Results'
                  )}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Physical Health Info Dialog */}
      <AlertDialog.Root open={isPhysicalHealthDialogOpen} onOpenChange={setIsPhysicalHealthDialogOpen}>
        <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto z-50">
            <AlertDialog.Title className="text-xl font-semibold mb-4">
              {physicalHealthDataExists ? 'Edit Physical Health Information' : 'Add Physical Health Information'}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-500 mb-6">
              {physicalHealthDataExists 
                ? 'Update the physical health check information for this patient.'
                : 'Please provide the physical health check information for this patient.'
              }
            </AlertDialog.Description>

            <div className="space-y-4">
              {physicalHealthFields
                .filter(field => field.varName !== 'bloodPressureDiastolic') // Filter out diastolic since it's rendered with systolic
                .map((field, index) => (
                  <div key={field.varName}>
                    {index > 0 && (
                      <div className="h-px bg-gray-200 my-4" aria-hidden="true" />
                    )}
                    {renderPhysicalHealthField(field)}
                  </div>
                ))}
            </div>

            {physicalHealthSubmitError && (
              <div className="p-3 mt-4 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
                {physicalHealthSubmitError}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-semibold rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300"
                  disabled={isPhysicalHealthSubmitting}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              
              <button
                type="button"
                onClick={handlePhysicalHealthSubmit}
                disabled={!isPhysicalHealthFormValid() || isPhysicalHealthSubmitting}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors
                  ${!isPhysicalHealthFormValid() || isPhysicalHealthSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 hover:bg-violet-700'}`}
              >
                {isPhysicalHealthSubmitting ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                                  ) : (
                    physicalHealthDataExists ? 'Update Physical Health Info' : 'Submit Physical Health Info'
                  )}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Export Data Dialog — rendered unconditionally so Radix Presence can
         animate the exit transition when `open` flips to false. */}
      <ExportWizardDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        patientDataModel={patientDataModel}
        patientId={patientId}
        informantData={informantData}
      />

      {/* Proforma JSON Export Dialog — only rendered for Well Life Clinic users */}
      {isProformaExportEnabled && (
        <ExportProformaDialog
          isOpen={isProformaExportOpen}
          onClose={() => setIsProformaExportOpen(false)}
          patientDataModel={patientDataModel}
          patientId={patientId}
        />
      )}

      {/* 
        Reassign Clinicians Dialog
        
        isLeadClinicianMode: When true, the user is the Lead Clinician (not admin/support).
        In this mode, the "Change Lead Clinician" action is disabled to prevent self-removal.
        The backend also enforces this restriction as defence in depth.
      */}
      <ReassignCliniciansDialog
        // Defence in depth: even if local open-state races with a takeover
        // becoming active (e.g. after a refresh), never show the dialog while
        // a Lead SSI override is in progress.
        isOpen={isReassignDialogOpen && !isLeadOverrideActive}
        onClose={() => {
          setIsReassignDialogOpen(false);
          onReassignStepHandled?.();
        }}
        patientId={patientId}
        patientDataModel={patientDataModel}
        refreshPatientData={refreshPatientData}
        isLeadClinicianMode={isLeadClinicianMode}
        initialStep={requestReassignStep || 'summary'}
      />

      {/* Initial clinician assignment dialog for patients created without clinicians */}
      <AssignCliniciansDialog
        isOpen={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        patientId={patientId}
        patientDataModel={patientDataModel}
        refreshPatientData={refreshPatientData}
      />

      {/* Manage Informants Dialog */}
      <ProviderManageInformantsForm
        isOpen={isManageInformantsOpen}
        onClose={() => setIsManageInformantsOpen(false)}
        patientDataModel={patientDataModel}
        patientId={patientId}
        refreshPatientData={refreshPatientData}
        reportContent={reportContent}
      />

      {/* Communications Timeline Dialog */}
      <MessageHistoryDialog
        isOpen={isMessageHistoryOpen}
        onClose={() => setIsMessageHistoryOpen(false)}
        patientId={patientId}
      />

      {/* Resend Welcome Email Confirmation Dialog */}
      <AlertDialog.Root open={isResendDialogOpen} onOpenChange={setIsResendDialogOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-md w-full z-50">
            <AlertDialog.Title className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MailIcon className="w-5 h-5 text-primary" />
              Resend Welcome Email
            </AlertDialog.Title>
            <AlertDialog.Description asChild>
              <div className="mt-3 space-y-3">
                <p className="text-sm text-gray-600">
                  This will send a new account setup email to{' '}
                  <span className="font-semibold text-gray-900">
                    {patientDataModel?.firstName} {patientDataModel?.lastName}
                  </span>{' '}
                  ({patientDataModel?.email}) with a link to set their password.
                </p>

                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                  {isLoadingLastSent ? (
                    <p className="text-xs text-gray-500">Checking email history...</p>
                  ) : lastWelcomeEmailSentAt ? (
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Last sent:</span>{' '}
                      {formatDateTime(lastWelcomeEmailSentAt)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Original email sent at account creation
                    </p>
                  )}
                </div>
              </div>
            </AlertDialog.Description>

            <div className="mt-5 flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-semibold rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  disabled={isResendLoading}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={handleResendConfirm}
                disabled={isResendLoading}
                className="px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {isResendLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Edit Patient Details Dialog */}
      <ProviderEditPatientForm
        isOpen={isEditPatientOpen}
        onClose={() => setIsEditPatientOpen(false)}
        patient={patientDataModel}
        patientId={patientId}
        onSuccess={refreshPatientData}
      />

    </>
  );
}

